import { useCallback, useEffect, useState } from 'react';

import {
  type PhxFeel,
  type PhxMode,
  type PhxScheme,
  applyPhxFeel,
  applyPhxMode,
  applyPhxScheme,
  readPhxFeel,
  readPhxMode,
  readPhxScheme,
} from './phxTheme';

export interface UsePhxThemeResult {
  mode: PhxMode;
  scheme: PhxScheme;
  feel: PhxFeel;
  setMode: (mode: PhxMode) => void;
  setScheme: (scheme: PhxScheme) => void;
  setFeel: (feel: PhxFeel) => void;
  toggleMode: () => void;
}

/**
 * React hook bridging the document-level Phoenix theme attributes with React
 * state.  The hook initialises from `localStorage` (and falls back to the
 * design-system defaults), persists every change, and exposes setters that
 * keep both the DOM attribute and the in-memory state in sync.
 */
export function usePhxTheme(): UsePhxThemeResult {
  const [mode, setModeState] = useState<PhxMode>(() => readPhxMode());
  const [scheme, setSchemeState] = useState<PhxScheme>(() => readPhxScheme());
  const [feel, setFeelState] = useState<PhxFeel>(() => readPhxFeel());

  useEffect(() => applyPhxMode(mode), [mode]);
  useEffect(() => applyPhxScheme(scheme), [scheme]);
  useEffect(() => applyPhxFeel(feel), [feel]);

  const setMode = useCallback((value: PhxMode) => setModeState(value), []);
  const setScheme = useCallback((value: PhxScheme) => setSchemeState(value), []);
  const setFeel = useCallback((value: PhxFeel) => setFeelState(value), []);
  const toggleMode = useCallback(
    () => setModeState((current) => (current === 'dark' ? 'light' : 'dark')),
    []
  );

  return { mode, scheme, feel, setMode, setScheme, setFeel, toggleMode };
}
